import {
  QUESTION_TYPE,
  assessmentMaxSelections,
  blankCount,
  clockConfig,
  getQuestionType,
  matchingLeftOptions,
  optionalQuestions,
  optionalRequiredCount,
  sharedOptionQuestions
} from '@/utils/questionDisplay'

const hasText = value => value !== null && typeof value !== 'undefined' && String(value).trim() !== ''
const itemContent = item => item && typeof item === 'object' ? item.content : item

export function createCanonicalAnswer(question = {}) {
  const type = getQuestionType(question)
  if ([QUESTION_TYPE.SINGLE, QUESTION_TYPE.JUDGE, QUESTION_TYPE.SHORT].includes(type)) return ''
  if ([QUESTION_TYPE.MULTI, QUESTION_TYPE.ASSESSMENT].includes(type)) return []
  if (type === QUESTION_TYPE.FILL) return Array.from({ length: blankCount(question) }, () => '')
  if (type === QUESTION_TYPE.MATCHING) {
    return matchingLeftOptions(question).map(item => ({ left: item.key, right: '' }))
  }
  if (type === QUESTION_TYPE.SHARED_OPTION) {
    return sharedOptionQuestions(question).map((item, index) => ({
      name: item.name || index + 1,
      content: ''
    }))
  }
  if (type === QUESTION_TYPE.CLOCK) {
    return clockConfig(question).map(group => ({
      type: group.type,
      data: Array.from({ length: group.time_count }, () => ({ period: '', hour: '', minute: '' }))
    }))
  }
  if (type === QUESTION_TYPE.WORD_SELECT) {
    return Array.from({ length: blankCount(question) }, (_, index) => ({ name: index + 1, content: '' }))
  }
  if (type === QUESTION_TYPE.OPTIONAL) {
    const subQuestions = optionalQuestions(question)
    const required = optionalRequiredCount(question)
    return subQuestions.map((item, index) => ({
      name: item.answer_name || index + 1,
      selected: subQuestions.length <= required,
      content: item.question_type === QUESTION_TYPE.MULTI ? [] : ''
    }))
  }
  return ''
}

export function isAnswerStarted(question = {}, answer) {
  const type = getQuestionType(question)
  if (type === QUESTION_TYPE.MATCHING) {
    return Array.isArray(answer) && answer.some(item => item && hasText(item.right))
  }
  if ([QUESTION_TYPE.SHARED_OPTION, QUESTION_TYPE.WORD_SELECT, QUESTION_TYPE.OPTIONAL].includes(type)) {
    return Array.isArray(answer) && answer.some(item => hasAnswerValue(itemContent(item)))
  }
  if (type === QUESTION_TYPE.CLOCK) {
    return Array.isArray(answer) && answer.some(group => Array.isArray(group && group.data) && group.data.some(time =>
      time && ['period', 'hour', 'minute'].some(field => hasText(time[field]))
    ))
  }
  return hasAnswerValue(answer)
}

export function isAnswerComplete(question = {}, answer) {
  const type = getQuestionType(question)
  if ([QUESTION_TYPE.SINGLE, QUESTION_TYPE.JUDGE, QUESTION_TYPE.SHORT].includes(type)) return hasText(answer)
  if ([QUESTION_TYPE.MULTI, QUESTION_TYPE.ASSESSMENT].includes(type)) {
    const max = type === QUESTION_TYPE.ASSESSMENT ? assessmentMaxSelections(question) : Infinity
    return Array.isArray(answer) && answer.length > 0 && answer.length <= max
  }
  if (type === QUESTION_TYPE.FILL) {
    return Array.isArray(answer) && answer.length === blankCount(question) && answer.every(hasText)
  }
  if (type === QUESTION_TYPE.MATCHING) {
    return Array.isArray(answer) && answer.length === matchingLeftOptions(question).length && answer.every(item =>
      item && hasText(item.left) && hasText(item.right)
    )
  }
  if ([QUESTION_TYPE.SHARED_OPTION, QUESTION_TYPE.WORD_SELECT].includes(type)) {
    const expected = type === QUESTION_TYPE.SHARED_OPTION ? sharedOptionQuestions(question).length : blankCount(question)
    return Array.isArray(answer) && answer.length === expected && answer.every(item => hasText(itemContent(item)))
  }
  if (type === QUESTION_TYPE.CLOCK) {
    const groups = clockConfig(question)
    return Array.isArray(answer) && answer.length === groups.length && answer.every((group, index) =>
      group && Array.isArray(group.data) && group.data.length === groups[index].time_count && group.data.every(time =>
        time && hasText(time.period) && hasText(time.hour) && hasText(time.minute)
      )
    )
  }
  if (type === QUESTION_TYPE.OPTIONAL) {
    if (!Array.isArray(answer)) return false
    const selected = answer.filter(item => item && item.selected)
    return selected.length === optionalRequiredCount(question) && selected.every(item => hasAnswerValue(item.content))
  }
  return hasAnswerValue(answer)
}

export function serializeSelfTestAnswer(question = {}, answer) {
  if (!isAnswerStarted(question, answer)) return null
  if (getQuestionType(question) === QUESTION_TYPE.OPTIONAL) {
    return answer.filter(item => item && item.selected).map(item => ({ name: item.name, content: item.content }))
  }
  return answer
}

export function serializeExamAnswer(question = {}, answer) {
  if (getQuestionType(question) === QUESTION_TYPE.OPTIONAL) {
    return Array.isArray(answer)
      ? answer.filter(item => item && item.selected).map(item => ({ name: item.name, content: item.content }))
      : []
  }
  return serializeHomeworkAnswer(question, answer)
}

export function serializeHomeworkAnswer(question = {}, answer) {
  const type = getQuestionType(question)
  if (type === QUESTION_TYPE.FILL) {
    return Array.isArray(answer) ? answer.map(item => String(item || '').trim()) : []
  }
  if (type === QUESTION_TYPE.MATCHING) {
    return Array.isArray(answer)
      ? answer.filter(item => item && hasText(item.right)).map(item => ({ left: item.left, right: item.right }))
      : []
  }
  if (type === QUESTION_TYPE.SHARED_OPTION) {
    const subQuestions = sharedOptionQuestions(question)
    return Array.isArray(answer) ? answer.map((item, index) => ({
      question_id: subQuestions[index] ? subQuestions[index].question_id : item.name,
      answer: item.content || ''
    })) : []
  }
  if (type === QUESTION_TYPE.WORD_SELECT) {
    return Array.isArray(answer) ? answer.map(item => String(itemContent(item) || '').trim()) : []
  }
  return answer
}

function hasAnswerValue(value) {
  if (Array.isArray(value)) return value.some(hasAnswerValue)
  if (value && typeof value === 'object') return Object.values(value).some(hasAnswerValue)
  return hasText(value)
}
